import { SsuLogoPlaceholder } from '@capsicum/common';
import { feedDataAtom } from '@capsicum/features/feeds/shared';
import { FeedTypes, type ObjectDataShape, type PartnerWithImageMap, type PostActor } from '@capsicum/socios-core';
import { useAtomValue } from 'jotai';
import { useCallback } from 'react';
import { match } from 'ts-pattern';

import { type PostMainPartner } from '../models';

type UseMainPartnerProps = {
	actor: PostActor;
	feedId: string;
	feedType: FeedTypes;
	isPostFromWP: boolean;
	/** Post data object */
	object: ObjectDataShape;
};

type RelatedToShape = {
	partner_page: Array<string>;
} | null;

const getRelatedToId = (relatedTo: RelatedToShape = null) => {
	if (!relatedTo) return '';

	return relatedTo?.partner_page?.[0] || '';
};

/**
 * This hook determines the primary partner in activities involving two partners, based on the current feed.
 * The decision logic varies with the feed, utilizing `feedType` and `feedId` for determination.
 *
 * `actor` and `relatedTo` represent the involved partners, where the actor is not always the primary partner. (Hence this hook)
 * The essence of this hook is to establish the primary partner by combining `feedType`, `feedId`, and the partner details.
 */
export const useMainPartner = ({ actor, feedId, feedType, isPostFromWP, object }: UseMainPartnerProps) => {
	const { favouritePartnerId, followedPartners, partnersData, walletsBalance } = useAtomValue(feedDataAtom);
	const isFavouriteTeamFeed = feedId === favouritePartnerId;
	const isActorFavouritePartner = actor.id === favouritePartnerId;
	const isActorSameAsFeedId = actor.id === feedId;
	const relatedToId = getRelatedToId(object?.relatedTo);
	const isActorFollowed = followedPartners?.some(partner => partner.id === actor.id);
	const isRelatedToFollowed = followedPartners?.some(partner => partner.id === relatedToId);

	const getActorData = () => ({
		id: actor.id || '',
		image: actor.image || (SsuLogoPlaceholder as unknown as string),
		name: actor.name || '',
		shortName: actor.shortName || ''
	});

	const getMainPartnerData = (partner: PartnerWithImageMap) => ({
		id: partner?.id || '',
		image: partner?.images?.partnerLogo?.url || (SsuLogoPlaceholder as unknown as string),
		name: partner?.name || '',
		shortName: partner?.shortName || ''
	});

	const getRelatedToData = () =>
		relatedToId
			? getMainPartnerData(
					partnersData?.getPartnerBy(partner => partner.id === relatedToId) as PartnerWithImageMap
				)
			: getActorData();

	const getPartnerWithMoreBalance = () => {
		const actorPartner = partnersData?.getPartnerBy(partner => partner.id === actor.id);
		const relatedToPartner = partnersData?.getPartnerBy(partner => partner.id === relatedToId);

		const getPartnerBalance = (partner: PartnerWithImageMap | undefined) => {
			const currencyId = partner?.currencyId || '';

			const balance =
				walletsBalance?.getCurrencyBy(currency => currency.currency.id === currencyId)?.totalAmount || 0;

			return Number(balance);
		};

		const actorBalance = getPartnerBalance(actorPartner);
		const relatedToBalance = getPartnerBalance(relatedToPartner);

		return actorBalance >= relatedToBalance
			? getActorData()
			: getMainPartnerData(relatedToPartner as PartnerWithImageMap);
	};

	const getFollowedTeamsFeedMainPartner = () => {
		if (isActorFavouritePartner || !isActorFollowed) return getRelatedToData();
		if (isActorFollowed && isRelatedToFollowed) return getPartnerWithMoreBalance();

		return getActorData();
	};

	const getSuggestedTeamsFeedMainPartner = () => {
		if (isActorFavouritePartner || isActorFollowed) return getRelatedToData();
		if (!isActorFollowed && !isRelatedToFollowed) return getPartnerWithMoreBalance();

		return getActorData();
	};

	const getMainPartner = useCallback((): PostMainPartner => {
		if (feedId) {
			if (isFavouriteTeamFeed && isPostFromWP) {
				return getActorData();
			}

			return isActorSameAsFeedId
				? getActorData()
				: getMainPartnerData(
						partnersData?.getPartnerBy(partner => partner.id === feedId) as PartnerWithImageMap
					);
		}

		return match(feedType)
			.with(FeedTypes.FOLLOWED_TEAMS, getFollowedTeamsFeedMainPartner)
			.with(FeedTypes.SUGGESTED_FEED, getSuggestedTeamsFeedMainPartner)
			.otherwise(getActorData);
	}, [
		feedId,
		getActorData,
		getMainPartnerData,
		getFollowedTeamsFeedMainPartner,
		isActorSameAsFeedId,
		isFavouriteTeamFeed,
		isPostFromWP,
		partnersData
	]);

	return { getMainPartner };
};
